import { memo, useMemo } from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import { useInteractions, useListItem } from '@floating-ui/react';

import { useIsInView } from '@/lib/hooks';

import { Exploration, CellViewOptions } from '../types';
import { Icon } from '../../components/icon';
import { CellNode } from '.';
import { useMetadataContext } from '../metadata-context';
import { PipelinePreview } from '../exploration/pipeline-preview';
import { ensureValidPipeline, getExplorationVariables } from '../utils';

import * as styles from './exploration-search.module.scss';

interface CellListProps {
  exploration: Exploration;
  activeIndex: number | null;
  items: CellNode[];
  getItemProps: ReturnType<typeof useInteractions>['getItemProps'];
  disabled?: boolean;
}

export const CellList = memo(function CellList(props: CellListProps) {
  return (
    <ol>
      {props.items.map((item) => (
        <CellItem
          key={item.cell.id}
          item={item}
          activeIndex={props.activeIndex}
          exploration={props.exploration}
          disabled={props.disabled}
          getItemProps={props.getItemProps}
        />
      ))}
    </ol>
  );
});

interface CellItemProps {
  exploration: Exploration;
  activeIndex: number | null;
  item: CellNode;
  getItemProps: ReturnType<typeof useInteractions>['getItemProps'];
  disabled?: boolean;
}

const CellItem = memo(function CellItem(props: CellItemProps) {
  const { item, exploration, activeIndex, getItemProps, disabled = false } = props;
  const { cell, getUrl } = item;
  const { pipeline } = cell;

  const { models, metrics } = useMetadataContext();
  const { ref, index } = useListItem();

  // Avoid calculating all the URLs in a list at first render
  const [inViewRef, inView] = useIsInView();
  const url = useMemo(() => {
    return inView ? getUrl() : '#';
  }, [getUrl, inView]);

  const validatedPipeline = useMemo(() => {
    if (!inView) {
      return pipeline;
    }
    return ensureValidPipeline(pipeline, {
      exploration,
      models,
      metrics,
      variables: getExplorationVariables(exploration),
    });
  }, [pipeline, exploration, models, metrics, inView]);

  return (
    <Link
      to={url}
      className={classNames(styles.listItem, { [styles.active]: activeIndex === index })}
      ref={(node) => {
        ref(node);
        inViewRef(node);
      }}
      aria-disabled={disabled}
      {...getItemProps({
        onClick: item.onClick,
      })}>
      <Icon name="Block" size={32} className={styles.icon} />
      <div className={styles.name}>{cell.title}</div>
      {!disabled && (
        <div className={styles.pipeline}>
          <PipelinePreview
            models={models}
            title={cell.title ?? 'Untitled'}
            exploration={exploration}
            pipeline={validatedPipeline}
            interactive={false}
            rollbackIndex={(cell.viewOptions as CellViewOptions | undefined)?.rollback?.index}
          />
        </div>
      )}
    </Link>
  );
});
